// ============================================================================
// CRM MODULE — data layer (real FastAPI + MongoDB backend, Phase 5)
// ----------------------------------------------------------------------------
// Read = view_crm; write = edit_crm. Store backend-hydrated + mutazioni
// ottimistiche (API sincrona invariata → i moduli CRM non cambiano). Scope:
// admin/manager/developer vedono tutto; team_member/specialist solo i propri
// contatti/opportunità. Automazioni EXECUTION mock (no WhatsApp/email/n8n reali).
// ============================================================================
import { listTeam } from "@/lib/teamData";
import { api } from "@/lib/api";

const now = () => new Date().toISOString();
const uid = (p) => `${p}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

export const OPP_STATUS = { open: { label: "Aperta", tone: "info" }, won: { label: "Vinta", tone: "ok" }, lost: { label: "Persa", tone: "warning" } };
export const CRM_CHANNELS = { email: "Email", whatsapp: "WhatsApp" };
export const CRM_TRIGGERS = {
  first_contact_email: "Email primo contatto", followup_email: "Email follow-up",
  proposal_sent_email: "Email proposta inviata", proposal_accepted_email: "Email proposta accettata",
  first_contact_wa: "WhatsApp primo contatto", followup_wa: "WhatsApp follow-up",
  won: "Opportunità vinta", lost: "Opportunità persa",
};
export const CRM_TEMPLATE_VARS = ["{{company_name}}", "{{contact_name}}", "{{email}}", "{{phone}}", "{{opportunity_title}}", "{{pipeline_name}}", "{{stage_name}}", "{{proposal_link}}", "{{assigned_user_name}}", "{{assigned_user_email}}", "{{assigned_user_phone}}"];
export const AUTOMATION_TRIGGERS = { stage_changed: "Opportunità spostata di stage", won: "Opportunità vinta", lost: "Opportunità persa", proposal_accepted: "Proposta accettata", proposal_sent: "Proposta inviata", followup_due: "Data follow-up raggiunta", appointment_scheduled: "Appuntamento fissato", contact_created: "Nuovo contatto creato" };
export const AUTOMATION_ACTIONS = { send_whatsapp: "Invia WhatsApp", send_email: "Invia email", create_task: "Crea task", schedule_appointment: "Fissa appuntamento", move_stage: "Sposta di stage", move_pipeline: "Sposta di pipeline", create_proposal: "Crea proposta", convert_client: "Converti in cliente", add_note: "Aggiungi nota", notify_member: "Notifica membro team" };
export const MESSAGING_ACTIONS = { send_whatsapp: "whatsapp", send_email: "email" };

const STAGE_COLORS = ["#6366f1", "#0ea5e9", "#f59e0b", "#10b981", "#ec4899", "#ef4444", "#8b5cf6"];

export const teamMembers = () => listTeam();
const memberName = (id) => listTeam().find((m) => m.id === id)?.name || "—";

const DEFAULT_SETTINGS = {
  source: ["meta_ads", "website", "referral", "manual", "event", "whatsapp", "partner", "cold_outreach", "other"],
  sector: ["medical", "aesthetic", "pharmacy", "hospitality", "restaurant", "retail", "real_estate", "consulting", "fitness", "other"],
  lead_type: ["prospect", "qualified", "partner", "existing_contact", "free_consultation", "trial", "other"],
  lost_reason: ["budget", "not_interested", "timing", "competitor", "no_response", "not_qualified", "other"],
};

// ---------------------------------------------------------------------------
// CACHE (backend-hydrated)
// ---------------------------------------------------------------------------
const empty = () => ({ pipelines: [], contacts: [], opportunities: [], timeline: [], automations: [], templates: [], settings: { ...DEFAULT_SETTINGS }, import_logs: [] });
let _store = empty();
let _hydrated = false;
const _subs = new Set();
const notify = () => _subs.forEach((fn) => { try { fn(); } catch { /* noop */ } });
export const subscribeCrm = (fn) => { _subs.add(fn); return () => _subs.delete(fn); };
export const isCrmReady = () => _hydrated;

export async function hydrateCrm() {
  try {
    const { data } = await api.get("/crm/store");
    _store = { ...empty(), ...data, settings: { ...DEFAULT_SETTINGS, ...(data.settings || {}) } };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("hydrateCrm failed", e?.response?.status);
  } finally { _hydrated = true; notify(); }
}
export const resetCrm = () => { _store = empty(); _hydrated = false; };
const resync = () => { hydrateCrm(); };
const read = () => _store;
const touch = () => { _store = { ..._store }; notify(); };
const persist = (promise) => { promise.catch(resync); };

const addActivityLocal = (opp, activity_type, title, description = "") => {
  _store.timeline = [{ id: uid("tl"), contact_id: opp?.contact_id || null, opportunity_id: opp?.id || null, activity_type, title, description, created_by: "me", created_at: now() }, ..._store.timeline];
};

// ---------------------------------------------------------------------------
// PIPELINES
// ---------------------------------------------------------------------------
export const listPipelines = () => read().pipelines;
export const getPipeline = (id) => read().pipelines.find((p) => p.id === id) || null;
export const newStage = (order) => ({ id: uid("stg"), stage_name: "Nuovo stage", stage_order: order, stage_color: STAGE_COLORS[order % STAGE_COLORS.length], probability: 50, is_won_stage: false, is_lost_stage: false });
export const savePipeline = (data) => {
  if (data.id) {
    _store.pipelines = _store.pipelines.map((p) => (p.id === data.id ? { ...p, ...data, updated_at: now() } : p)); touch();
    persist(api.patch(`/crm/pipelines/${data.id}`, data));
    return data.id;
  }
  const id = uid("pl");
  const doc = { id, pipeline_name: data.pipeline_name || "Nuova pipeline", description: data.description || "", default_currency: data.default_currency || "EUR", status: data.status || "active", stages: data.stages || [newStage(0)], visibility: data.visibility || { mode: "all", member_ids: [] }, created_at: now(), updated_at: now() };
  _store.pipelines = [..._store.pipelines, doc]; touch();
  persist(api.post("/crm/pipelines", { id, ...doc }));
  return id;
};
export const duplicatePipeline = (id) => {
  const p = getPipeline(id); if (!p) return;
  const nid = uid("pl");
  _store.pipelines = [..._store.pipelines, { ...p, id: nid, pipeline_name: `${p.pipeline_name} (copia)`, stages: p.stages.map((st) => ({ ...st, id: uid("stg") })) }]; touch();
  persist(api.post(`/crm/pipelines/${id}/duplicate`).then(({ data }) => { _store.pipelines = _store.pipelines.map((x) => (x.id === nid ? data : x)); touch(); }));
};
export const deletePipeline = (id) => { _store.pipelines = _store.pipelines.filter((p) => p.id !== id); touch(); persist(api.delete(`/crm/pipelines/${id}`)); };

// ---------------------------------------------------------------------------
// OPPORTUNITIES
// ---------------------------------------------------------------------------
export const listOpportunities = (pipelineId) => read().opportunities.filter((o) => !pipelineId || o.pipeline_id === pipelineId);
export const getOpportunity = (id) => read().opportunities.find((o) => o.id === id) || null;
export const createOpportunity = (data) => {
  const id = uid("op");
  const o = { id, item_kind: data.item_kind || "lead", contact_id: data.contact_id || null, client_id: data.client_id || null, company_name: data.company_name || "", contact_name: data.contact_name || "", email: data.email || "", phone: data.phone || "", city: data.city || "", sector: data.sector || "", source: data.source || "", lead_type: data.lead_type || "", opportunity_title: data.opportunity_title || "Nuova opportunità", pipeline_id: data.pipeline_id, stage_id: data.stage_id, value: Number(data.value) || 0, probability: Number(data.probability) || 0, expected_close_date: data.expected_close_date || null, assigned_to_user_id: data.assigned_to_user_id || null, assigned_to_user_name: memberName(data.assigned_to_user_id), status: "open", next_followup_date: data.next_followup_date || null, due_date: data.due_date || null, notes: data.notes || "", won_at: null, lost_at: null, lost_reason: null, created_at: now(), updated_at: now() };
  _store.opportunities = [..._store.opportunities, o]; addActivityLocal(o, "note", data.item_kind === "task" ? "Attività creata" : "Opportunità creata"); touch();
  persist(api.post("/crm/opportunities", { id, ...o }));
  return id;
};
export const updateOpportunity = (id, patch) => {
  _store.opportunities = _store.opportunities.map((o) => (o.id === id ? { ...o, ...patch, assigned_to_user_name: memberName(patch.assigned_to_user_id || o.assigned_to_user_id), updated_at: now() } : o)); touch();
  persist(api.patch(`/crm/opportunities/${id}`, patch));
};
export const moveOpportunity = (id, pipelineId, stageId, note) => {
  const o = _store.opportunities.find((x) => x.id === id); if (!o) return Promise.resolve(null);
  const changedPipe = o.pipeline_id !== pipelineId;
  _store.opportunities = _store.opportunities.map((x) => (x.id === id ? { ...x, pipeline_id: pipelineId, stage_id: stageId, updated_at: now() } : x));
  addActivityLocal(o, changedPipe ? "pipeline_change" : "stage_change", changedPipe ? "Spostata di pipeline" : "Spostata di stage", note || ""); touch();
  // Ritorna la risposta del server: contiene `automation_runs` (id dei run attivati)
  // usati dal loader in board per mostrare l'automazione in esecuzione.
  return api.post(`/crm/opportunities/${id}/move`, { pipeline_id: pipelineId, stage_id: stageId, note })
    .then((r) => r.data)
    .catch(() => { resync(); return null; });
};
// Stato dei run automazione attivati (polling loader board).
export const fetchAutomationRuns = (ids) =>
  api.get("/crm/automation-runs", { params: { ids: ids.join(",") } }).then((r) => r.data);
// Esegue subito i job automazione in coda (pulsante refresh board).
export const kickAutomations = () => api.post("/crm/automations/kick").then((r) => r.data).catch(() => null);
export const markWon = (id, opts = {}) => {
  const o = _store.opportunities.find((x) => x.id === id); if (!o) return;
  _store.opportunities = _store.opportunities.map((x) => (x.id === id ? { ...x, status: "won", won_at: now(), updated_at: now() } : x));
  addActivityLocal(o, "won", "Opportunità vinta", opts.notes || ""); touch();
  persist(api.post(`/crm/opportunities/${id}/won`, { notes: opts.notes || "" }));
};
export const markLost = (id, reason, notes) => {
  const o = _store.opportunities.find((x) => x.id === id); if (!o) return;
  _store.opportunities = _store.opportunities.map((x) => (x.id === id ? { ...x, status: "lost", lost_at: now(), lost_reason: reason, updated_at: now() } : x));
  addActivityLocal(o, "lost", "Opportunità persa", notes || ""); touch();
  persist(api.post(`/crm/opportunities/${id}/lost`, { reason, notes: notes || "" }));
};
export const deleteOpportunity = (id) => { _store.opportunities = _store.opportunities.filter((o) => o.id !== id); touch(); persist(api.delete(`/crm/opportunities/${id}`)); };
export const logComm = (oppId, channel, title) => {
  const o = _store.opportunities.find((x) => x.id === oppId);
  addActivityLocal(o, channel === "whatsapp" ? "whatsapp_sent" : channel === "email" ? "email_sent" : channel, title); touch();
  persist(api.post(`/crm/opportunities/${oppId}/comm`, { channel, title }));
};
export const addNote = (oppId, text) => {
  const o = _store.opportunities.find((x) => x.id === oppId);
  addActivityLocal(o, "note", "Nota", text); touch();
  persist(api.post(`/crm/opportunities/${oppId}/note`, { text }));
};
export const oppTimeline = (oppId) => read().timeline.filter((t) => t.opportunity_id === oppId).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
export const removeTimelineItem = (id) => {
  _store.timeline = _store.timeline.filter((t) => t.id !== id);
  touch();
  persist(api.delete(`/crm/timeline/${id}`).catch(() => {}));
};

// ---------------------------------------------------------------------------
// CONTACTS
// ---------------------------------------------------------------------------
export const listContacts = () => read().contacts;
export const getContact = (id) => read().contacts.find((c) => c.id === id) || null;
const PROSPECT_FIELDS = ["structure_type", "province", "website", "socials", "health_department", "health_evidence",
  "meta_ads_active", "meta_ads_library_url", "contact_role", "source_distributor", "next_action", "next_action_date"];
export const saveContact = (data) => {
  if (data.id) {
    _store.contacts = _store.contacts.map((c) => (c.id === data.id ? { ...c, ...data, assigned_to_user_name: memberName(data.assigned_to_user_id || c.assigned_to_user_id), updated_at: now() } : c)); touch();
    persist(api.patch(`/crm/contacts/${data.id}`, data));
    return data.id;
  }
  const id = uid("ct");
  const c = { id, company_name: data.company_name || "", contact_name: data.contact_name || "", email: data.email || "", phone: data.phone || "", city: data.city || "", sector: data.sector || "", source: data.source || "", lead_type: data.lead_type || "", notes: data.notes || "", assigned_to_user_id: data.assigned_to_user_id || null, assigned_to_user_name: memberName(data.assigned_to_user_id), status: "active", created_at: now(), updated_at: now() };
  PROSPECT_FIELDS.forEach((k) => { if (data[k] !== undefined && data[k] !== "") c[k] = data[k]; });
  _store.contacts = [..._store.contacts, c]; addActivityLocal({ contact_id: id }, "note", "Contatto creato"); touch();
  persist(api.post("/crm/contacts", { id, ...c }));
  return id;
};
// Sovrascrive un contatto esistente con i nuovi dati (usato quando l'utente conferma
// "sovrascrivi" nel dialog duplicato). Preserva l'id e i timestamp originari lato server.
export const overwriteContact = (existingId, data) => {
  _store.contacts = _store.contacts.map((c) => (c.id === existingId
    ? { ...c, ...data, id: existingId,
        assigned_to_user_name: memberName(data.assigned_to_user_id || c.assigned_to_user_id),
        updated_at: now() }
    : c));
  touch();
  persist(api.patch(`/crm/contacts/${existingId}`, data));
  return existingId;
};
export const deleteContact = (id) => { _store.contacts = _store.contacts.filter((c) => c.id !== id); touch(); persist(api.delete(`/crm/contacts/${id}`)); };

// Import massivo con strategy e report. Ritorna una Promise con
// {counts:{created,updated,duplicated,skipped,failed}, details:[...], log_id}.
export const importContacts = async (rows, opts = {}) => {
  const strategy = opts.strategy || "skip";
  const filename = opts.filename || "";
  try {
    const { data } = await api.post("/crm/contacts/import",
      { rows, strategy, filename });
    // Refresh completo del CRM per riflettere i contatti creati/aggiornati.
    await hydrateCrm();
    return { ok: true, log_id: data.log_id, ...data.report };
  } catch (e) {
    return { ok: false, error: e?.response?.data?.detail || "Import fallito",
             counts: { created: 0, updated: 0, duplicated: 0, skipped: 0, failed: rows.length },
             details: rows.map((r, i) => ({ row: r._line || i + 2, name: r.company_name || r.contact_name,
                                             status: "failed", reason: "Errore di rete" })) };
  }
};
export const contactOpportunities = (contactId) => read().opportunities.filter((o) => o.contact_id === contactId);
// Duplicati: opportunità APERTA dello stesso contatto nella stessa pipeline.
// NON si applica ai lead del Lead Router (deduplica tecnica separata su external_lead_id).
export const findOpenOppInPipeline = (contactId, pipelineId) =>
  read().opportunities.find((o) => o.contact_id === contactId && o.pipeline_id === pipelineId && o.status === "open") || null;

// Dedupe contatti in rubrica: match su email o telefono normalizzato (case-insensitive,
// senza spazi). Ritorna il PRIMO contatto duplicato, escludendo se stessi (excludeId).
const _normKey = (s) => (s || "").toString().trim().toLowerCase().replace(/\s+/g, "");
export const findDuplicateContact = (data, excludeId = null) => {
  const email = _normKey(data.email);
  const phone = _normKey(data.phone);
  if (!email && !phone) return null;
  return read().contacts.find((c) => {
    if (excludeId && c.id === excludeId) return null;
    const ce = _normKey(c.email);
    const cp = _normKey(c.phone);
    return (email && ce === email) || (phone && cp === phone);
  }) || null;
};

// -------- Import logs ----------------------------------------------------------
export const listImportLogs = () => (read().import_logs || []).slice().sort((a, b) =>
  new Date(b.created_at) - new Date(a.created_at));
export const appendImportLog = (log) => {
  const doc = { id: uid("imlog"), created_at: now(), ...log };
  _store.import_logs = [doc, ...(_store.import_logs || [])];
  touch();
  persist(api.post("/crm/import-logs", {
    type: doc.type, filename: doc.filename, strategy: doc.strategy,
    counts: doc.counts, details: doc.details,
  }).catch(() => {}));
  return doc.id;
};

export const convertToClient = () => true; // futura creazione in Client Registry

// ---------------------------------------------------------------------------
// AUTOMATIONS
// ---------------------------------------------------------------------------
export const listAutomations = () => read().automations;
export const saveAutomation = (data) => {
  if (data.id) {
    _store.automations = _store.automations.map((a) => (a.id === data.id ? { ...a, ...data, updated_at: now() } : a)); touch();
    persist(api.patch(`/crm/automations/${data.id}`, data));
    return data.id;
  }
  const id = uid("au");
  const doc = { id, automation_name: data.automation_name || "Nuova automazione", pipeline_id: data.pipeline_id || null, trigger_type: data.trigger_type || "contact_created", stage_id: data.stage_id || null, conditions: data.conditions || [], actions: data.actions || [], is_active: data.is_active !== false, created_at: now(), updated_at: now() };
  _store.automations = [..._store.automations, doc]; touch();
  persist(api.post("/crm/automations", { id, ...doc }));
  return id;
};
export const deleteAutomation = (id) => { _store.automations = _store.automations.filter((a) => a.id !== id); touch(); persist(api.delete(`/crm/automations/${id}`)); };
export const stageAutomations = (pipelineId, stageId) => read().automations.filter((a) => a.pipeline_id === pipelineId && a.trigger_type === "stage_changed" && a.stage_id === stageId);
export const templatesForChannel = (channel) => read().templates.filter((t) => t.channel === channel);

// ---------------------------------------------------------------------------
// TEMPLATES (CRM — gestione separata)
// ---------------------------------------------------------------------------
export const listCrmTemplates = () => read().templates;
export const saveCrmTemplate = (data) => {
  if (data.id) {
    _store.templates = _store.templates.map((t) => (t.id === data.id ? { ...t, ...data, updated_at: now() } : t)); touch();
    persist(api.patch(`/crm/templates/${data.id}`, data));
    return data.id;
  }
  const id = uid("ctpl");
  const doc = { id, template_name: data.template_name || "Nuovo modello", channel: data.channel || "email", trigger: data.trigger || "first_contact_email", subject: data.subject || "", body: data.body || "", sender_id: data.sender_id || null, is_active: data.is_active !== false, created_at: now(), updated_at: now() };
  _store.templates = [..._store.templates, doc]; touch();
  persist(api.post("/crm/templates", { id, ...doc }));
  return id;
};
export const deleteCrmTemplate = (id) => { _store.templates = _store.templates.filter((t) => t.id !== id); touch(); persist(api.delete(`/crm/templates/${id}`)); };

// ---------------------------------------------------------------------------
// SETTINGS VALUES
// ---------------------------------------------------------------------------
export const getSettingValues = (type) => read().settings[type] || [];
export const addSettingValue = (type, value) => {
  const v = (value || "").trim(); if (!v) return;
  const cur = _store.settings[type] || [];
  if (!cur.includes(v)) { _store.settings = { ..._store.settings, [type]: [...cur, v] }; touch(); }
  persist(api.post(`/crm/settings/${type}`, { value: v }));
};
export const removeSettingValue = (type, value) => {
  _store.settings = { ..._store.settings, [type]: (_store.settings[type] || []).filter((x) => x !== value) }; touch();
  persist(api.delete(`/crm/settings/${type}/${encodeURIComponent(value)}`));
};

// ---------------------------------------------------------------------------
// KPIs
// ---------------------------------------------------------------------------
export const crmKpis = () => {
  // Ignora le opportunità "orfane" (pipeline eliminata): non compaiono in nessun
  // tabellone, quindi non devono nemmeno gonfiare le card. Così i numeri restano
  // coerenti con le pipeline realmente esistenti.
  const pipelineIds = new Set(read().pipelines.map((p) => p.id));
  const o = read().opportunities.filter((x) => pipelineIds.has(x.pipeline_id));
  const c = read().contacts;
  const thisMonth = new Date().toISOString().slice(0, 7);
  const today = new Date().toISOString().slice(0, 10);
  return {
    open: o.filter((x) => x.status === "open").length,
    won: o.filter((x) => x.status === "won").length,
    lost: o.filter((x) => x.status === "lost").length,
    pipelineValue: o.filter((x) => x.status === "open").reduce((a, x) => a + (x.value || 0), 0),
    followupsDue: o.filter((x) => x.status === "open" && x.next_followup_date && x.next_followup_date <= today).length,
    newContacts: c.filter((x) => (x.created_at || "").slice(0, 7) === thisMonth).length,
    proposalsSent: read().timeline.filter((t) => t.activity_type === "proposal_sent").length,
    proposalsAccepted: read().timeline.filter((t) => t.activity_type === "proposal_accepted").length,
  };
};


// ---------------------------------------------------------------------------
// LISTE, TAG ed EXPORT (Sotto-blocco 1)
// ---------------------------------------------------------------------------
export const listLists = () => read().lists || [];
export const listTags = () => read().tags || [];
export const canExportContacts = () => !!read().can_export;

export const createList = (name) => {
  const tmp = { id: uid("lst"), name, description: "", created_at: now() };
  _store.lists = [...(_store.lists || []), tmp]; touch();
  persist(api.post("/crm/lists", { name }).then(({ data }) => { _store.lists = (_store.lists || []).map((l) => l.id === tmp.id ? data : l); touch(); }));
};
export const renameList = (id, name) => {
  _store.lists = (_store.lists || []).map((l) => l.id === id ? { ...l, name } : l); touch();
  persist(api.patch(`/crm/lists/${id}`, { name }));
};
export const deleteList = (id) => {
  _store.lists = (_store.lists || []).filter((l) => l.id !== id);
  _store.contacts = (_store.contacts || []).map((c) => ({ ...c, list_ids: (c.list_ids || []).filter((x) => x !== id) })); touch();
  persist(api.delete(`/crm/lists/${id}`));
};
export const setContactList = (contactId, listId, add) => {
  _store.contacts = (_store.contacts || []).map((c) => c.id === contactId ? { ...c, list_ids: add ? [...new Set([...(c.list_ids || []), listId])] : (c.list_ids || []).filter((x) => x !== listId) } : c); touch();
  persist(api.post(`/crm/lists/${listId}/contacts`, { contact_id: contactId, action: add ? "add" : "remove" }));
};

export const createTag = (name, color = "slate") => {
  const tmp = { id: uid("tag"), name, color, created_at: now() };
  _store.tags = [...(_store.tags || []), tmp]; touch();
  persist(api.post("/crm/tags", { name, color }).then(({ data }) => { _store.tags = (_store.tags || []).map((t) => t.id === tmp.id ? data : t); touch(); }));
};
export const deleteTag = (id) => {
  _store.tags = (_store.tags || []).filter((t) => t.id !== id);
  _store.contacts = (_store.contacts || []).map((c) => ({ ...c, tag_ids: (c.tag_ids || []).filter((x) => x !== id) })); touch();
  persist(api.delete(`/crm/tags/${id}`));
};
export const setContactTags = (contactId, tagIds) => {
  _store.contacts = (_store.contacts || []).map((c) => c.id === contactId ? { ...c, tag_ids: tagIds } : c); touch();
  persist(api.post(`/crm/contacts/${contactId}/tags`, { tag_ids: tagIds }));
};

export const exportContactsCsv = async () => {
  const res = await api.get("/crm/contacts/export", { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const a = document.createElement("a"); a.href = url; a.download = "contatti_crm.csv"; a.click();
  window.URL.revokeObjectURL(url);
};
